import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { uniq } from 'lodash-es';

/**
 * SQL predicate restricting results to a saved list's members. The API expands
 * each `{{list:<id>}}` placeholder at query time, so the result stays in sync
 * with the list rather than freezing a snapshot of its ids.
 */
export function toListReferencePredicate(listId: WorkspaceItem['id']): string {
  return `"internal_id" IN ({{list:${listId}}})`;
}

export function toListReferenceSql(listId: WorkspaceItem['id']): string {
  return `SELECT * FROM data_table WHERE ${toListReferencePredicate(listId)}`;
}

/* Inverse of `toListReferenceSql`; must be kept in sync with it. */
const listReferenceSqlRegex =
  /^SELECT \* FROM data_table WHERE "internal_id" IN \(\{\{list:([^}]+)\}\}\)$/;

/**
 * Returns the id of the list that `sql` references, if `sql` is exactly a bare
 * list reference as built by `toListReferenceSql`. Arbitrary SQL that merely
 * contains a `{{list:<id>}}` placeholder does not qualify.
 */
export function getReferencedListId(
  sql: string | undefined | null
): WorkspaceItem['id'] | undefined {
  return sql?.match(listReferenceSqlRegex)?.[1];
}

const listPlaceholderRegex = /\{\{list:([^}]+)\}\}/g;

/**
 * Returns the ids of all lists that `sql` references through `{{list:<id>}}`
 * placeholders, wherever they appear (e.g. in AI-authored SQL).
 */
export function findReferencedListIds(
  sql: string | undefined | null
): WorkspaceItem['id'][] {
  if (!sql) return [];

  return uniq(Array.from(sql.matchAll(listPlaceholderRegex), ([, id]) => id));
}
